import { forwardRef, useLayoutEffect, useMemo, useState } from 'react'
import { mergeRefs } from 'react-merge-refs'
import { ZIndex } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import { Button, Tooltip } from '@opallabs/lapidary'
import { ColorLabelToken } from '@opallabs/lapidary/src/components'
import {
  DateField,
  SingleSelectField,
  TextField,
  FieldInputProps,
} from '../../types'
import * as styles from './styles'

const colorTokenSelector = '[data-field-color-token]'

export type TextDisplayProps = Pick<
  FieldInputProps<DateField | TextField | SingleSelectField>,
  'readOnly' | 'setIsEditing'
> & {
  fieldId: string
  fieldLabel: string
  /** Drawn in front of the text, whether or not there is any. */
  leadingNode?: React.ReactNode
  lineClamp?: number
  text?: string | null
  color?: string | null
  colorsEnabled?: boolean
  /** the selected option has been deleted; render it faded. */
  isUnavailable?: boolean
}

export const TextDisplay = forwardRef<HTMLButtonElement, TextDisplayProps>(
  (
    {
      fieldId,
      fieldLabel,
      leadingNode,
      lineClamp,
      readOnly,
      setIsEditing,
      text,
      color,
      colorsEnabled = false,
      isUnavailable = false,
    },
    ref
  ) => {
    const theme = useTheme()
    const [trigger, setTrigger] = useState<HTMLElement | null>(null)
    const buttonRef = useMemo(() => mergeRefs([setTrigger, ref]), [ref])
    const [isTokenTruncated, setIsTokenTruncated] = useState(false)
    const colorLabel =
      colorsEnabled && color && text ? { color, label: text } : null
    const showsColorToken = colorLabel !== null
    const tooltipDisabled = showsColorToken
      ? !isTokenTruncated
      : !text || !lineClamp || text.length < 40 * lineClamp

    // Measured before paint, so `disabled` is current when focus or a
    // pointer opens the tooltip.
    useLayoutEffect(() => {
      const token = showsColorToken
        ? trigger?.querySelector(colorTokenSelector)
        : null
      if (!token) {
        setIsTokenTruncated(false)
        return
      }

      const measure = () =>
        setIsTokenTruncated(token.scrollWidth > token.clientWidth)
      measure()
      const observer = new ResizeObserver(measure)
      observer.observe(token)
      return () => observer.disconnect()
    }, [trigger, showsColorToken, text, color])

    if (readOnly) {
      return (
        <Tooltip
          content={text ?? ''}
          disabled={tooltipDisabled}
          zIndex={ZIndex.Drawer.Tooltip}
        >
          <div
            ref={setTrigger}
            css={styles.readOnlyWrapper(!tooltipDisabled, theme)}
          >
            {leadingNode}
            {colorLabel ? (
              <ColorLabelToken
                {...colorLabel}
                data-field-color-token
                css={[styles.colorToken, isUnavailable && styles.unavailable]}
              />
            ) : (
              <span
                css={[
                  styles.readOnly({
                    hasValue: Boolean(text),
                    lineClamp,
                    theme,
                  }),
                  isUnavailable && styles.unavailable,
                ]}
              >
                {text ?? 'None'}
              </span>
            )}
          </div>
        </Tooltip>
      )
    }

    return (
      <Tooltip
        content={text ?? ''}
        disabled={tooltipDisabled}
        zIndex={ZIndex.Drawer.Tooltip}
      >
        <Button
          ref={buttonRef}
          id={`field-${fieldId}`}
          css={styles.buttonStyles({
            colorsEnabled,
            hasLeadingNode: leadingNode !== undefined && leadingNode !== null,
            lineClamp,
            theme,
          })}
          onClick={() => setIsEditing(true)}
          hideButtonText={true}
          buttonText={text ?? 'None'}
          ariaLabel={`Edit ${fieldLabel}`}
          name={`Edit ${fieldLabel}`}
          variant="ghost"
        >
          {leadingNode}
          {colorLabel ? (
            <ColorLabelToken
              {...colorLabel}
              data-field-color-token
              css={[styles.colorToken, isUnavailable && styles.unavailable]}
            />
          ) : (
            <span
              css={[
                styles.readOnly({
                  hasValue: Boolean(text),
                  lineClamp,
                  theme,
                }),
                !colorsEnabled && styles.compactLineHeight,
                isUnavailable && styles.unavailable,
              ]}
            >
              {text ?? 'None'}
            </span>
          )}
        </Button>
      </Tooltip>
    )
  }
)

TextDisplay.displayName = 'TextDisplay'
